import { useState } from "react";
import { createPortal } from "react-dom";
import Twemoji from "react-twemoji";
import "overlayscrollbars/overlayscrollbars.css";

import { PuzzleType } from "sockapi/puzzle-data";
import { ScopeType } from "sockapi/scope-types";
import { TeamPuzzleSockAPIScopeState } from "sockapi/scope-specs/team-spec";
import {
  doesSolutionExist,
  isSolutionPageAvailable,
} from "sockapi/scope-specs/solution-content-spec";
import {
  HINT_CONFIG,
  isPuzzleEligibleForHint,
} from "sockapi/scope-specs/team-hints-spec";
import { useInPuzzlePage } from "stores/NavStore";
import {
  useTeamId,
  useShouldDisplayAdmin,
} from "stores/ServerInteractionStore";
import {
  useUpdateBrowserWidePersistentState,
  makeDefaultCutsceneState,
} from "stores/BrowserWidePersistentState";
import {
  useScopeStateResolvable,
  useTeamScopeState,
  useIsPuzzleSolved,
  isPuzzleTeamSolved,
  useIsHuntOver,
  useNumTotalHintTokens,
} from "stores/ScopeStateStore";
import { PuzzleLink } from "components/Link";
import { Metal } from "components/Material";
import Tooltip from "components/Tooltip";
import { usePuzzleBodyDependencies } from "pages/PuzzlePage";
import "./PuzzlePageToolbar.css";

export function useShowSolution(
  puzData: TeamPuzzleSockAPIScopeState
): boolean | null {
  const { puzName } = puzData;
  const isHuntOver = useIsHuntOver();
  const isSolved = useIsPuzzleSolved(puzName);
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const teamScopeState = useTeamScopeState();
  if (isHuntOver === null || isSolved === null || teamScopeState === null)
    return null;
  const isFinished = teamScopeState.finishedData !== undefined;
  return (
    doesSolutionExist(puzData) &&
    (isHuntOver ||
      isSolutionPageAvailable(puzName, isSolved, isFinished) ||
      shouldDisplayAdmin)
  );
}

export function useShowHints(
  puzData: TeamPuzzleSockAPIScopeState
): boolean | null {
  const { puzName } = puzData;
  const teamId = useTeamId();
  const teamCannedHintsScopeState = useScopeStateResolvable(
    ScopeType.TEAM_CANNED_HINTS,
    teamId === null ? null : { teamId }
  );
  const teamHintsScopeState = useScopeStateResolvable(
    ScopeType.TEAM_HINTS,
    teamId === null ? null : { teamId }
  );
  const numTotalHintTokens = useNumTotalHintTokens(teamId);
  if (
    teamCannedHintsScopeState === null ||
    teamHintsScopeState === null ||
    numTotalHintTokens === null
  )
    return null;
  const numTotalCannedHints =
    teamCannedHintsScopeState.cannedHints[puzName]?.numTotal ?? 0;
  const numUsedHints = Object.keys(
    teamHintsScopeState.hints[puzName] ?? {}
  ).length;
  return (
    isPuzzleEligibleForHint(puzData) &&
    (numTotalCannedHints > 0 || numTotalHintTokens > 0 || numUsedHints > 0)
  );
}

export function useShowStats(
  puzData: TeamPuzzleSockAPIScopeState
): boolean | null {
  const isHuntOver = useIsHuntOver();
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  if (isHuntOver === null) return null;
  return (
    puzData.type === PuzzleType.PUZZLE && (isHuntOver || shouldDisplayAdmin)
  );
}

export function useShowHuntStats(): boolean | null {
  const isHuntOver = useIsHuntOver();
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  if (isHuntOver === null) return null;
  return isHuntOver || shouldDisplayAdmin;
}

/** Currently portaled to WorldBase toolbar... */
export default function PuzzlePageToolbar(props: {
  puzData: TeamPuzzleSockAPIScopeState;
}) {
  const { puzData } = props;
  const { puzName } = puzData;
  const deps = usePuzzleBodyDependencies(puzData);
  const updateBrowserWidePersistentState =
    useUpdateBrowserWidePersistentState();
  const showSolution = useShowSolution(puzData) ?? false;
  const showHints = useShowHints(puzData) ?? false;
  const showStats = useShowStats(puzData) ?? false;
  const [{ inPuzzle, inSolution, inHints, inStats }] =
    useState(useInPuzzlePage());

  const { cutsceneState, isPuzzleSolved } = deps ?? {};
  const outlet = document.getElementById("world-base-puzzle-toolbar-container");
  if (!outlet) return null;
  const hintEligible = isPuzzleEligibleForHint(puzData);

  return createPortal(
    <>
      {showSolution || showHints || hintEligible || showStats ? (
        <PuzzleLink puzName={puzName} disabled={inPuzzle}>
          <Tooltip text="Puzzle">
            <Metal
              rotate={0}
              seed="PuzzlePageToolbar: puzzle link"
              active={inPuzzle}
            >
              <Twemoji>🔲️</Twemoji>
            </Metal>
          </Tooltip>
        </PuzzleLink>
      ) : null}
      {showSolution ? (
        <PuzzleLink puzName={puzName} subpage="solution" disabled={inSolution}>
          <Tooltip text="Solution">
            <Metal
              rotate={0}
              seed="PuzzlePageToolbar: solution link"
              active={inSolution}
            >
              <Twemoji>🏁</Twemoji>
            </Metal>
          </Tooltip>
        </PuzzleLink>
      ) : null}
      {showHints ? (
        <PuzzleLink puzName={puzName} subpage="hints" disabled={inHints}>
          <Tooltip text="Hints">
            <Metal
              rotate={0}
              seed="PuzzlePageToolbar: hints link"
              active={inHints}
            >
              <Twemoji>🍞️</Twemoji>
            </Metal>
          </Tooltip>
        </PuzzleLink>
      ) : hintEligible ? (
        <Tooltip
          text={`Hints are not currently available.\n\nHints are given out to all teams every ${(HINT_CONFIG.timedHintInterval * 2) / 60 / 60 / 1000} hours until the hunt ends.`}
        >
          <Metal
            rotate={0}
            seed="PuzzlePageToolbar: hints link"
            active={inHints}
          >
            <Twemoji>🍞️</Twemoji>
          </Metal>
        </Tooltip>
      ) : null}
      {showStats ? (
        <PuzzleLink puzName={puzName} subpage="stats" disabled={inStats}>
          <Tooltip text="Stats">
            <Metal
              rotate={0}
              seed="PuzzlePageToolbar: stats link"
              active={inStats}
            >
              <Twemoji>🧮️</Twemoji>
            </Metal>
          </Tooltip>
        </PuzzleLink>
      ) : null}
      {(cutsceneState?.historyPosition ?? null) != null ? (
        isPuzzleTeamSolved(puzData) ? (
          // If the puzzle has been solved by the team but not
          // locally, give the user the option to skip it.
          <a
            href="#"
            onClick={(evt) => {
              evt.preventDefault();
              updateBrowserWidePersistentState({
                completedStoryBeats: { [puzName]: true },
                cutscenesState: {
                  [puzName]: makeDefaultCutsceneState(),
                },
              });
            }}
          >
            <Tooltip text={isPuzzleSolved ? "Exit" : "Skip"}>
              <Metal rotate={0} seed="PuzzlePageToolbar: cutscene">
                <Twemoji>🏃🏿‍➡️</Twemoji>
              </Metal>
            </Tooltip>
          </a>
        ) : (
          <a
            href="#"
            onClick={(evt) => {
              evt.preventDefault();
              updateBrowserWidePersistentState({
                cutscenesState: {
                  [puzName]: makeDefaultCutsceneState(),
                },
              });
            }}
          >
            <Tooltip text="Reset">
              <Metal rotate={0} seed="PuzzlePageToolbar: cutscene">
                <Twemoji>🏃🏿</Twemoji>
              </Metal>
            </Tooltip>
          </a>
        )
      ) : null}
    </>,
    outlet
  );
}
